import React from 'react';
import { Loader2, Save } from 'lucide-react';
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import type {
  BillableStatus,
  BillingCycleSupplyItem,
  BillingCycleVisitItem,
  BillingCycleVisitsAndSuppliesResponse,
  EoePreviewOrderItem,
} from '../../../services/api/types.gen';
import { formatDateToMMDDYYYY } from '../../../utils/dateUtils';
import { isValidDollarInput } from '../../../utils/currencyUtils';
import { NewTabIconLink } from '../NewTabIconLink';
import { billingOrderPath, patientVisitPath } from '../../../utils/navigation';
import { STATUS_DISPLAY_LABELS } from '../../task/taskStatus';

/** Unset/unknown statuses are treated as BILLABLE so the Select shows a value. */
function visitBillableGroupKey(
  status: string | null | undefined
): 'BILLABLE' | 'NON_BILLABLE' | 'NON_COVERED' {
  const s = (status ?? '').trim().toUpperCase();
  if (s === 'NON_BILLABLE') return 'NON_BILLABLE';
  if (s === 'NON_COVERED') return 'NON_COVERED';
  return 'BILLABLE';
}

const displayDate = (value?: string | null): string =>
  formatDateToMMDDYYYY(value) || '-';

const displayValue = (value?: string | number | null): string =>
  value === null || value === undefined || value === '' ? '-' : String(value);

const thClass = 'py-1.5 pr-4 font-medium';

/**
 * Visit billable-status + bulk-supply-cost editor, folded into the claim
 * worksheet (formerly the standalone EOEClaimsWorksheet). Styled to match the
 * Claim Preview's section / table vocabulary. Presentational: the parent owns
 * the pending state and the save (`updateBillingWorksheet`). When `readOnly`,
 * billable-status selects and the bulk-cost input disable; visit chart links
 * stay active.
 */
export const VisitSupplyBillableSection: React.FC<{
  visitsData: BillingCycleVisitsAndSuppliesResponse | null;
  orders: EoePreviewOrderItem[];
  patientId: string;
  episodeId: string;
  readOnly?: boolean;
  canViewOrders: boolean;
  pendingBillingStatusChanges: Record<string, BillableStatus>;
  onBillableStatusChange: (visitId: string, status: BillableStatus) => void;
  pendingBulkSupplyCost: string | null;
  onBulkSupplyCostChange: (raw: string) => void;
  onSaveSourceData?: () => void;
  isSavingSourceData?: boolean;
}> = ({
  visitsData,
  orders,
  patientId,
  episodeId,
  readOnly = false,
  canViewOrders,
  pendingBillingStatusChanges,
  onBillableStatusChange,
  pendingBulkSupplyCost,
  onBulkSupplyCostChange,
  onSaveSourceData,
  isSavingSourceData = false,
}) => {
  const visits = visitsData?.visits ?? [];
  const supplies = visitsData?.supplies ?? [];

  return (
    <>
      <section>
        <div className='mb-2 flex items-center justify-between gap-3'>
          <div>
            <h3 className='text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
              Source data
            </h3>
            <p className='text-xs text-muted-foreground'>
              Save source changes, then refresh worksheet data to rebuild
              service lines.
            </p>
          </div>
          {onSaveSourceData ? (
            <Button
              type='button'
              variant='outline'
              size='sm'
              disabled={readOnly || isSavingSourceData}
              onClick={onSaveSourceData}
            >
              {isSavingSourceData ? (
                <Loader2 className='mr-1 h-3 w-3 animate-spin' />
              ) : (
                <Save className='mr-1 h-3 w-3' />
              )}
              Save source data
            </Button>
          ) : null}
        </div>
        <h3 className='mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
          Visits
        </h3>
        {visits.length === 0 ? (
          <p className='text-sm text-muted-foreground'>
            No visits for this billing cycle.
          </p>
        ) : (
          <table className='w-full text-sm'>
            <thead>
              <tr className='border-b text-left text-xs text-muted-foreground'>
                <th className={thClass}>Discipline</th>
                <th className={thClass}>Visit date</th>
                <th className={thClass}>G code</th>
                <th className={thClass}>Q code</th>
                <th className={thClass}>Status</th>
                <th className={thClass}>Units</th>
                <th className={thClass}>Charge</th>
                <th className={thClass}>Billable status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {visits.map((visit: BillingCycleVisitItem) => (
                <tr
                  key={visit.visit_id}
                  className='border-b border-border/50 align-middle'
                >
                  <td className='py-1.5 pr-4'>
                    {displayValue(visit.discipline)}
                  </td>
                  <td className='py-1.5 pr-4 whitespace-nowrap'>
                    {displayDate(visit.visit_date)}
                  </td>
                  <td className='py-1.5 pr-4 font-mono'>
                    {displayValue(visit.hcpcs_g_code)}
                  </td>
                  <td className='py-1.5 pr-4 font-mono'>
                    {displayValue(visit.hcpcs_q_code)}
                  </td>
                  <td className='py-1.5 pr-4'>
                    {displayValue(visit.visit_status)}
                  </td>
                  <td className='py-1.5 pr-4'>
                    {displayValue(visit.units)}
                    {visit.needs_visit_times && (
                      <span
                        className='ml-1 cursor-help text-amber-600'
                        title='Missing or unusable visit start/end times — units and charge cannot be computed, and the claim will be rejected until the chart is fixed.'
                      >
                        ⚠
                      </span>
                    )}
                  </td>
                  <td className='py-1.5 pr-4'>
                    {displayValue(visit.charge)}
                    {visit.missing_rate && !visit.needs_visit_times && (
                      <span
                        className='ml-1 cursor-help text-muted-foreground'
                        title='No insurance rate row for this visit type — codes and charge basis come from the plan defaults.'
                      >
                        *
                      </span>
                    )}
                  </td>
                  <td className='py-1.5'>
                    <Select
                      value={
                        pendingBillingStatusChanges[visit.visit_id] ??
                        visitBillableGroupKey(visit.billable_status)
                      }
                      onValueChange={value =>
                        onBillableStatusChange(
                          visit.visit_id,
                          value as BillableStatus
                        )
                      }
                      disabled={readOnly}
                    >
                      <SelectTrigger selectSize='sm' className='w-36'>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value='BILLABLE'>Billable</SelectItem>
                        <SelectItem value='NON_BILLABLE'>
                          Non-Billable
                        </SelectItem>
                        <SelectItem value='NON_COVERED'>Non-Covered</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className='py-1.5 pl-2 text-right'>
                    <NewTabIconLink
                      href={patientVisitPath(
                        patientId,
                        visit.visit_id,
                        episodeId
                      )}
                      label='View chart'
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section>
        <h3 className='mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
          Orders
        </h3>
        {orders.length === 0 ? (
          <p className='text-sm text-muted-foreground'>
            No billing orders found.
          </p>
        ) : (
          <table className='w-full text-sm'>
            <thead>
              <tr className='border-b text-left text-xs text-muted-foreground'>
                <th className={thClass}>Order</th>
                <th className={thClass}>Date</th>
                <th className={thClass}>Status</th>
                <th className={thClass}>Physician</th>
                <th className={thClass}>For billing</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order: EoePreviewOrderItem) => (
                <tr
                  key={order.order_id}
                  className='border-b border-border/50 align-middle'
                >
                  <td className='py-1.5 pr-4'>
                    {displayValue(order.order_type_display)}
                  </td>
                  <td className='py-1.5 pr-4 whitespace-nowrap'>
                    {displayDate(order.order_date)}
                  </td>
                  <td className='py-1.5 pr-4'>
                    {displayValue(
                      STATUS_DISPLAY_LABELS[order.status] ?? order.status
                    )}
                  </td>
                  <td className='py-1.5 pr-4'>
                    {displayValue(order.physician_name)}
                  </td>
                  <td className='py-1.5 pr-4'>
                    {order.required_for_billing ? 'Required' : 'Optional'}
                  </td>
                  <td className='py-1.5 pl-2 text-right'>
                    {canViewOrders ? (
                      <NewTabIconLink
                        href={billingOrderPath({
                          orderId: order.order_id,
                          status: order.status,
                          patientId,
                          episodeId,
                        })}
                        label='View order'
                      />
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section>
        <h3 className='mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
          Supplies
        </h3>
        {supplies.length === 0 ? (
          <p className='text-sm text-muted-foreground'>
            No supplies for this billing cycle.
          </p>
        ) : (
          <table className='w-full text-sm'>
            <thead>
              <tr className='border-b text-left text-xs text-muted-foreground'>
                <th className={thClass}>Date</th>
                <th className={thClass}>Supply</th>
                <th className={thClass}>Quantity</th>
                <th className={thClass}>Unit cost</th>
                <th className={thClass}>Total</th>
                <th className='py-1.5 font-medium'>Billable status</th>
              </tr>
            </thead>
            <tbody>
              {supplies.map((supply: BillingCycleSupplyItem, index: number) => (
                <tr key={index} className='border-b border-border/50'>
                  <td className='py-1.5 pr-4 whitespace-nowrap'>
                    {displayDate(supply.supply_date)}
                  </td>
                  <td className='py-1.5 pr-4'>{displayValue(supply.supply)}</td>
                  <td className='py-1.5 pr-4'>
                    {displayValue(supply.quantity)}
                  </td>
                  <td className='py-1.5 pr-4'>
                    {displayValue(supply.unit_cost)}
                  </td>
                  <td className='py-1.5 pr-4'>{displayValue(supply.total)}</td>
                  <td className='py-1.5'>
                    {displayValue(supply.billable_status)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className='mt-3 flex items-center gap-3'>
          <span className='text-xs text-muted-foreground'>
            Bulk supply charge ($)
          </span>
          <Input
            inputSize='sm'
            type='text'
            inputMode='decimal'
            placeholder='0.00'
            className='w-36'
            disabled={readOnly}
            value={
              pendingBulkSupplyCost ??
              (visitsData?.bulk_supply_cost != null
                ? String(visitsData.bulk_supply_cost)
                : '')
            }
            onChange={e => {
              const raw = e.target.value;
              if (isValidDollarInput(raw)) {
                onBulkSupplyCostChange(raw);
              }
            }}
          />
        </div>
      </section>
    </>
  );
};
